import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, Home } from 'lucide-react';
import { getErrorMessage, getErrorTitle } from '@/lib/errorMessages';
import { useFirebaseAuthLogin } from './useFirebaseAuthLogin';
import { signInWithEmailPassword, signInWithGoogle, signInWithFacebook } from '@/config/firebase';
import { authApi } from './api';
import { saveAuthData } from '@/core/auth';
import { useSession } from '@/store/session';

const FIREBASE_ACCOUNT_MISSING_CODES = ['auth/user-not-found', 'auth/invalid-credential', 'auth/wrong-password'];

const loginSchema = z.object({
  email: z.string().email('Correo electrónico inválido').min(1, 'El correo electrónico es obligatorio'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin, isLoading: isCompletingLogin } = useFirebaseAuthLogin();
  const { setSession } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'facebook' | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const redirectState = (location.state || {}) as {
    returnTo?: string;
    animalId?: number;
    shelterId?: number;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const goToDestination = () => {
    const destination = redirectState.returnTo || '/';
    const nextState =
      redirectState.animalId || redirectState.shelterId
        ? { animalId: redirectState.animalId, shelterId: redirectState.shelterId }
        : undefined;
    navigate(destination, nextState ? { state: nextState } : undefined);
  };

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      let user;
      try {
        const idToken = await signInWithEmailPassword(data.email, data.password);
        user = await completeLogin(idToken);
      } catch (firebaseError: any) {
        // Legacy account not yet migrated to Firebase: fall back to the
        // bcrypt login, which migrates it transparently on success.
        if (!FIREBASE_ACCOUNT_MISSING_CODES.includes(firebaseError?.code)) {
          throw firebaseError;
        }
        const token = await authApi.login(data);
        sessionStorage.setItem('auth_token', token);
        user = await authApi.me();
        saveAuthData(token, user);
        setSession(token, user);
      }

      toast({
        title: '¡Bienvenido!',
        description: `Hola ${user.full_name || (user as any).fullName || user.email}`,
      });

      goToDestination();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: getErrorTitle(error),
        description: getErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoLogin = async (provider: 'google' | 'facebook') => {
    setSsoLoading(provider);
    try {
      const idToken = await (provider === 'google' ? signInWithGoogle() : signInWithFacebook());
      const user = await completeLogin(idToken);

      toast({
        title: '¡Bienvenido!',
        description: `Hola ${user.full_name || (user as any).fullName || user.email}`,
      });

      goToDestination();
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user') return;
      toast({
        variant: 'destructive',
        title: getErrorTitle(error),
        description: getErrorMessage(error),
      });
    } finally {
      setSsoLoading(null);
    }
  };

  const busy = isLoading || isCompletingLogin || !!ssoLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <div className="w-full max-w-md">
        {/* Link para Home */}
        <div className="flex justify-center mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 text-sm text-muted-foreground hover:text-foreground hover:bg-card transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Home className="h-4 w-4" />
            <span>Volver al inicio</span>
          </Link>
        </div>

        <Card className="w-full max-w-md p-8 shadow-xl">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo-sabuesos.png" alt="Sabuesos" className="w-16 h-16 mb-4 object-contain" />
            <h1 className="text-3xl font-bold text-foreground">Sabuesos</h1>
            <p className="text-muted-foreground mt-2">Sistema de Gestión de Adopciones</p>
          </div>

          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => handleSsoLogin('google')}
            >
              {ssoLoading === 'google' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Continuar con Google
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => handleSsoLogin('facebook')}
            >
              {ssoLoading === 'facebook' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Continuar con Facebook
            </Button>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">o con tu correo</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                {...register('email')}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('password')}
                  aria-invalid={!!errors.password}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
              <Link
                to="/signup"
                className="text-sm text-primary hover:underline"
              >
                Crear cuenta
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={busy}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
